import { z } from "zod";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { createPhoneSchema } from "@/lib/validation/phone";

export const createAccountLocalisedSchema = (locale: Locales, accountType: "individual" | "company" = "individual") => {
  const addressNameSchema = z
    .string({ required_error: __("Address name is required", locale) })
    .trim()
    .min(3, __("Invalid address name", locale));

  const postalAddressSchema = z
    .string({ required_error: __("Postal address is required", locale) })
    .trim()
    .min(3, __("Invalid postal address", locale));

  const postalCodeSchema = z
    .string({ required_error: __("ZIP Code is required", locale) })
    .trim()
    .min(4, __("Invalid ZIP Code", locale));

  const citySchema = z
    .string({ required_error: __("City is required", locale) })
    .trim()
    .min(3, __("Invalid city", locale));

  const optionalMessageSchema = z.string().optional();
  const deliveryLocationTypeSchema = z.string().optional();
  const deliveryFloorSchema = z.string().optional();
  const deliveryHasElevatorSchema = z.enum(["yes", "no"]).optional();
  const optionalYesNoSchema = z.enum(["yes", "no"]).optional();

  const companyNameSchema =
    accountType === "company"
      ? z
          .string({ required_error: __("Company name is required", locale) })
          .trim()
          .min(1, __("Invalid company name", locale))
      : z.string().optional();

  const vatNumberSchema =
    accountType === "company"
      ? z
          .string({ required_error: __("VAT number is required", locale) })
          .trim()
          .min(1, __("Invalid VAT number", locale))
      : z.string().optional();

  const contactPersonSchema = z
    .string({ required_error: __("Contact person is required", locale) })
    .trim()
    .min(3, __("Invalid name", locale));

  const firstNameSchema = z
    .string({
      required_error: __("First name is required", locale),
    })
    .trim()
    .min(3, __("Invalid first name", locale));

  const lastNameSchema = z
    .string({
      required_error: __("Last name is required", locale),
    })
    .trim()
    .min(3, __("Invalid last name", locale));

  const emailSchema = z
    .string({
      required_error: __("Email is required", locale),
    })
    .trim()
    .email(__("Invalid email", locale));

  const phoneSchema = createPhoneSchema({
    required: __("Phone number is required", locale),
    invalid: __("Invalid phone number", locale),
  });

  const titleSchema = z.enum(["mademoiselle", "madame", "monsieur"], {
    required_error: __("Civility is required", locale),
  });
  const birthDateSchema = z.string().optional();
  const countryCodeSchema = z.string().optional();

  const shippingAddressSchema = z.object({
    addressName: addressNameSchema,
    postalAddress: postalAddressSchema,
    postalCode: postalCodeSchema,
    city: citySchema,
    message: optionalMessageSchema,
    countryCode: countryCodeSchema,
    deliveryLocationType: deliveryLocationTypeSchema,
    deliveryFloor: deliveryFloorSchema,
    deliveryHasElevator: deliveryHasElevatorSchema,
    deliveryInstructions: optionalMessageSchema,
    deliveryElevatorDimensions: optionalMessageSchema,
    deliveryElevatorDoorWidth: optionalMessageSchema,
    deliveryHasStairs: optionalYesNoSchema,
    deliveryStairsCount: optionalMessageSchema,
    deliveryHasGroundLevelAccess: optionalYesNoSchema,
    deliveryAccessDetails: optionalMessageSchema,
    deliveryParkingType: optionalMessageSchema,
    deliveryParkingDetails: optionalMessageSchema,
    deliveryContactName: optionalMessageSchema,
    deliveryContactPhone: optionalMessageSchema,
  });

  const billingAddressSchema = shippingAddressSchema.extend({
    companyName: companyNameSchema,
    vatNumber: vatNumberSchema,
    contactPerson: contactPersonSchema,
  });

  const inviteEmployeeSchema = z.object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    email: emailSchema,
  });

  const editPersonalInfoSchema = z.object({
    title: titleSchema,
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    email: emailSchema,
    phone: phoneSchema,
    birthDate: birthDateSchema,
  });

  const createCompanySchema = z.object({
    companyName: companyNameSchema,
    email: emailSchema,
    phone: phoneSchema,
    address: postalAddressSchema,
    city: citySchema,
    zip: postalCodeSchema,
    vat: vatNumberSchema,
  });

  return {
    editPersonalInfoSchema,
    inviteEmployeeSchema,
    shippingAddressSchema,
    billingAddressSchema,
    createCompanySchema,
  };
};

export type BillingAddressSchema = z.infer<ReturnType<typeof createAccountLocalisedSchema>["billingAddressSchema"]>;
export type ShippingAddressSchema = z.infer<ReturnType<typeof createAccountLocalisedSchema>["shippingAddressSchema"]>;
export type InviteEmployeeSchema = z.infer<ReturnType<typeof createAccountLocalisedSchema>["inviteEmployeeSchema"]>;
export type EditPersonalInfoSchema = z.infer<ReturnType<typeof createAccountLocalisedSchema>["editPersonalInfoSchema"]>;
export type CreateCompanySchema = z.infer<ReturnType<typeof createAccountLocalisedSchema>["createCompanySchema"]>;
